// src/components/common/audio-player/core/useAudioEngine.ts
//
// Created by Hossein Sakkaki on 2026-06-24.
// Last Update by Hossein Sakkaki on 2026-06-24.

import { useEffect, useRef } from "react";
import { logger } from "../../../../utils/logging/logger";

type Controls = {
  setPlaying: (v: boolean) => void;
  setDuration: (v: number) => void;
  setTime: (v: number) => void;
  setBufferedEnd: (v: number) => void;
};

type Options = {
  src: string;
  resolvedSrc: string;
  useShaka: boolean;
  autoPlay?: boolean;
  onReady?: () => void;
  onEnded?: () => void;
  ensureCookies?: () => Promise<void>;
};

export function useAudioEngine(
  audioRef: React.RefObject<HTMLAudioElement>,
  opts: Options,
  controls: Controls,
) {
  const playerRef = useRef<any>(null);
  const shakaRef = useRef<any>(null);
  const didRetryAuthRef = useRef(false);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    el.muted = false;
    el.volume = 1;
    el.crossOrigin = "anonymous";
    el.preload = "metadata";
    el.setAttribute("playsinline", "true");

    let cancelled = false;

    const retryWithFreshCookies = async () => {
      if (didRetryAuthRef.current) {
        return false;
      }

      didRetryAuthRef.current = true;

      try {
        await opts.ensureCookies?.();
        return true;
      } catch (error) {
        logger.error("[Audio] cookie refresh failed", error);
        return false;
      }
    };

    const onLoaded = () => {
      controls.setDuration(el.duration || 0);

      try {
        if (el.buffered?.length) {
          controls.setBufferedEnd(el.buffered.end(el.buffered.length - 1));
        }
      } catch {}

      controls.setPlaying(!el.paused);

      if (opts.autoPlay) {
        el.play().catch(() => {});
      }

      opts.onReady?.();
    };

    const onProgress = () => {
      try {
        if (el.buffered?.length) {
          controls.setBufferedEnd(el.buffered.end(el.buffered.length - 1));
        }
      } catch {}
    };

    const onTime = () => controls.setTime(el.currentTime || 0);
    const onPlay = () => controls.setPlaying(true);
    const onPause = () => controls.setPlaying(false);

    const onEnd = () => {
      controls.setPlaying(false);
      opts.onEnded?.();
    };

    const onAudioError = async () => {
      if (opts.useShaka) return;

      const currentTime = el.currentTime || 0;
      const canRetry = await retryWithFreshCookies();

      if (!canRetry || cancelled) {
        return;
      }

      try {
        el.src = opts.resolvedSrc;
        el.load();
        el.currentTime = currentTime;
        el.play().catch(() => {});
      } catch (error) {
        logger.error("[Audio] native retry failed", error);
      }
    };

    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("canplay", onLoaded);
    el.addEventListener("progress", onProgress);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnd);
    el.addEventListener("error", onAudioError);

    if (el.readyState >= 1) {
      queueMicrotask(onLoaded);
    }

    (async () => {
      try {
        didRetryAuthRef.current = false;

        if (opts.ensureCookies) {
          try {
            await opts.ensureCookies();
          } catch {}
        }

        if (opts.useShaka) {
          const shaka = (await import("shaka-player/dist/shaka-player.ui"))
            .default;

          shakaRef.current = shaka;
          shaka.polyfill.installAll();

          if (!cancelled) {
            const player = new shaka.Player();
            playerRef.current = player;

            player.addEventListener("error", async (event: any) => {
              const err = event?.detail;
              if (!err) return;

              const isAuthError =
                err.code === shaka.util.Error.Code.BAD_HTTP_STATUS &&
                (err.data?.[1] === 401 || err.data?.[1] === 403);

              if (!isAuthError) {
                return;
              }

              const currentTime = el.currentTime || 0;
              const canRetry = await retryWithFreshCookies();

              if (!canRetry || cancelled) {
                return;
              }

              try {
                await player.load(opts.resolvedSrc, currentTime);
                el.play().catch(() => {});
              } catch (error) {
                logger.error("[Audio] Shaka retry failed", error);
              }
            });

            await player.attach(el);
            await player.load(opts.resolvedSrc);
            controls.setPlaying(!el.paused);
          }
        } else {
          el.src = opts.resolvedSrc;
          el.load();
          controls.setPlaying(!el.paused);
        }
      } catch (error: any) {
        logger.error("[Audio] init/load error", {
          error: error?.message,
        });
      }
    })();

    return () => {
      cancelled = true;

      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("canplay", onLoaded);
      el.removeEventListener("progress", onProgress);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("error", onAudioError);

      try {
        playerRef.current?.destroy?.();
      } catch {}

      playerRef.current = null;

      el.removeAttribute("src");

      try {
        el.load();
      } catch {}
    };
  }, [audioRef, opts, controls]);
}